import React from "react";
import _ from "lodash";
import moment from "moment";

import { Service } from "./service";
import { esgProfileStepsData} from "./mock_esg_profile";
import { QuestionnairesHandlerResource } from "../../utils/api";
import {getDanteCustomerFullName} from "./utils";
import {getOriginLink} from "../../utils/constants";
import {esgProfileStepsDataV2} from "./components/servicesV2/mock_esg_data";
import {displaySuccessSnackBar, displayWarningSnackBar} from "../../components/SnackbarProvider/actions";

export class ESGProfileService extends Service {
  constructor(
    uid,
    name,
    next_btn,
    success_text,
    getPrevStepAnswer
  ) {
    super(
      uid,
      name,
      next_btn,
      success_text,
      getPrevStepAnswer
    );
    this._use_session = true;
    this._esgProfiles = [];
    this.skipStepsIfNotInterested = [
      "esg-info-profile",
      "ecologically-sustainable",
      "esg",
      "pai",
      "summary"
    ]
  }

  get isInterestedInESG(){
    return this.getStepAnswer('esg-profile-selection-step', "esg-profile-radio-uid");
  }

  get advisorChoice() {
    return this.getStepAnswer('esg-profile-selection-step', "esg-advisor-type");
  }

  get esgProfiles() {
    return this._esgProfiles;
  }

  async __sendStepData(isFinished, step = undefined) {
    if (this.__onboarding_uid) {

      if (this._step.uid === 'summary' && !this.lastESGProfile) {
        await QuestionnairesHandlerResource.at('set_esg_profile/').post({
          onboarding_uid: this.__onboarding_uid,
        });
      }

      return await super.__sendStepData(isFinished, step);
    }
  }

  removeESGSteps(uids=this.skipStepsIfNotInterested) {
    this._questionnaire.steps = this._questionnaire.steps.filter(s => {
      return !uids.includes(s.uid)
    })
  }

  addESGSteps(uids=this.skipStepsIfNotInterested, addAfterUID='esg-profile-selection-step') {
    let stepsUIDs = this._questionnaire.steps && this._questionnaire.steps.map(item => item && item.uid);
    let indexForInsert = stepsUIDs.indexOf(addAfterUID) + 1;
    const stepsData = this._getEsgStepsData().steps;
    uids.forEach(step => {
      if (!stepsUIDs.includes(step)) {
        this._questionnaire.steps.splice(indexForInsert, 0, stepsData.find(s => s.uid === step));
      }

      indexForInsert += 1;
    });
  }

  get isFistStep() {
    return !!this.lastESGProfile || super.isFistStep;
  }

  async __setQuestionnaire(onboardingAnswers) {
    await super.__setQuestionnaire(onboardingAnswers);
    // in case resendResult keep prev flag
    if(onboardingAnswers) {
      if(this.isNewDesign){
        // we removed this step
        this.skipStepsIfNotInterested = this.skipStepsIfNotInterested.filter(i => i !== 'esg-info-profile');
      }
    }
    this._questionnaire = this._getEsgStepsData();
    this.lastESGProfile = undefined;
    this.__keepStep = false;
    const hasESGAnswers = _.get(onboardingAnswers, this.__onboarding_step_uid);

    // Add Profiles Selection for New process
    try {
      this._esgProfiles = await QuestionnairesHandlerResource.at('get-esg-profiles/').get({customer_id: this._customer_id}) || [];
      this._esgProfiles = _.reverse(_.sortBy(this._esgProfiles, [(a) => moment(a.createdAt.split(" ")[0])]));

      if(this.isNewDesign && this._use_session && this._esgProfiles.length && !hasESGAnswers){
        this._step = this._questionnaire.steps[0];
        this.lastESGProfile = this._esgProfiles[0].esgProfileGuid;
        // set last esg profile as "selected" answer
        this.findQuestionDeep(this.step, 'esg-profile-radio-uid').answer = 'select';
        this.findQuestionDeep(this.step, 'esg-profile-select-uid').answer = this.lastESGProfile;
        // next step to prefill and store answers in db
        await this.nextStep();
        this.__keepStep = true;
        return;
      }
    } catch (e) {
      console.error(e);
    }

    // logic for 34f brokers
    if (_.get(this.user, 'bca_level_id') === '0') {
      const esgProfileRadioQuestion = this.getQuestion('esg-profile-selection-step', 'esg-profile-radio-uid');
      if (esgProfileRadioQuestion) {
        esgProfileRadioQuestion.question_text = "Profilauswahl";
      }
    }

    // FIXME: find better way set answers from db for the 1st step
    this._setDefaultAnswers(onboardingAnswers);
    // -> we need 1st step answers to check prefill from existing EsgProfile
    await this.prefillFromExistingProfile(() => {
      if(this.isNewDesign){
        this.goToStep("summary");
        this.__keepStep = true;
      }
    });

    // set answers from db
    this._setDefaultAnswers(onboardingAnswers);

    // in case we have answers for curr service - we should re-init step based on them
    if(hasESGAnswers) {
      this.setEsgProfileSteps(true);
    }
  }

  async prefillFromExistingProfile(useExistingCallback) {
    if(this.advisorChoice !== "no"){
        const esgRadio = this.isInterestedInESG;
        if (["prefill", "select"].includes(esgRadio)){
          // get origin session
          const esgProfileGUID = this.getStepAnswer("esg-profile-selection-step", "esg-profile-select-uid");
          if(esgProfileGUID) {
            const answers = await QuestionnairesHandlerResource.at('get-esg-profile-details/').get({
              customer_id: this.customer_id, token: esgProfileGUID
            });
            if (!_.isEmpty(answers)) {
              // set ESG profile answers
              this._questionnaire.steps.map((step) => {
                // do NOT fill profile selection step
                if (step.uid !== 'esg-profile-selection-step') {
                  const stepAnswers = answers[step.uid] || [];
                  this._setDefaultQuestionsAnswers(step, stepAnswers);
                }
              })
            }

            if (esgRadio === "select" && useExistingCallback) {
              await useExistingCallback();
            }
          }
        }
      }
  }

  setEsgProfileSteps(init=false){
    const esgProfileRadio = this.isInterestedInESG;
    if (this.advisorChoice === 'no' || _.isNil(esgProfileRadio) || esgProfileRadio === 'not-interested') {
      this.removeESGSteps()
    } else {
      this.addESGSteps();
      if(init){
        this._checkForZeroProfile()
      }
    }
  }

  async nextStep(onlySendData=false) {
    const isEsgSelectionStep = this.step.uid === "esg-profile-selection-step";
    let goToSummary = false;

    if(isEsgSelectionStep){
      await this.prefillFromExistingProfile(() => {
        goToSummary = true;
      })
    } else {
      this._checkForZeroProfile();
    }

    // recalculate _isLastStep
    await this.__setStep(0);

    const res = await super.nextStep();

    if(goToSummary){
      // FIXME: quick solution - jump to summary. We should skip steps instead
      // go to "summary" step
      this.goToStep("summary");
    }

    return res;
  }

  get _successLink(){
    return <a href={getOriginLink(`/#/Kunden/${this._customer_id}/Dokumente`)} target="_blank">Dokumentenablage</a>
  }

  get success_body() {
    return (
      <>
        Das Nachhaltigkeitsprofil {this.is_couple ? 'der Familie' : 'des Kunden'}  wurde erfolgreich gespeichert. Sie können diese
        Informationen auch unter {this._successLink} ansehen.
      </>
    )
  }

  getFinishSnackbarAction(){
    return this.getHasMissingAnswers()
      ? displayWarningSnackBar('Nachhaltigkeitsprofil unvollständig: Bitte füllen Sie alle Pflichtfelder aus, um die Erstellung abzuschließen.')
      : displaySuccessSnackBar(
        <>
          Das Nachhaltigkeitsprofil {this.is_couple ? 'der Gemeinschaft' : 'des Kunden'} wurde erfolgreich gespeichert. Sie finden die
          zugehörigen Informationen auch in der&nbsp;{this._successLink}&nbsp;Ihre{this.is_couple ? 'r' : 's'} Kunden.
        </>,
        30
      )
  }

  async confirmResult(data) {
    this._is_finished = true;
  }

  _getEsgStepsData(){
    const questionnaire = this.__deepCopy(this.isNewDesign ? esgProfileStepsDataV2 : esgProfileStepsData);

    if(this.isNewDesign){
      questionnaire.steps.forEach(step => {
        if(step.withUserName) {
          step.name = step.name.replace('{customer_name}', getDanteCustomerFullName(this.current_customer));
        }
      })
    }

    return questionnaire;
  }

  _checkForZeroProfile(){
    const esg = this.getStepAnswer('esg','percent');
    const eco = this.getStepAnswer('ecologically-sustainable','percent');
    const pai = this.getStepAnswer('pai','percent');

    if (eco === 0 && esg === 0 && pai === 0) {
      // on new design we should show error if "no esg data"
      if(this.isNewDesign && _.get(this.step, 'uid') === "pai"){
        throw Error(
          'Bitte befüllen Sie mindestens einen Schritt von der Profilierung aus (Prozentwert > 0 % und min. ' +
          'ein Kriterium wählen). Wenn Sie keine Profilierung wünschen, wählen Sie ' +
          '“Der Kunde wünscht keine Nachhaltigkeitsprofilierung.”')
      }

      this.removeESGSteps(["summary"])
    } else {
      this.addESGSteps(["summary"], "pai")
    }
  }

  get nextBtnLabel(){
    return this.isNewDesign && _.get(this.step, 'uid') === "summary" && "Bestätigen";
  }

  get resendResultBtnLabel(){
    return this.isNewDesign && _.get(this.step, 'uid') === "summary" && "Nachhaltigkeitsprofil";
  }

  async resendResult() {
    const res = await QuestionnairesHandlerResource.at('reset_esg_profile_data/').post({
      customer_id: this.customer_id,
      onboarding_uid: this.__onboarding_uid,
    });
    this._use_session = false;
    await this.initQuestionnaire(res.onboarding_answers);
  }

}